/**
 * Haptic Feedback Manager
 * Provides vibration feedback for user interactions
 */

class HapticManager {
  private enabled: boolean = true;

  /**
   * Check if vibration API is supported
   */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'vibrate' in navigator;
  }

  /**
   * Enable haptic feedback
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable haptic feedback
   */
  disable(): void {
    this.enabled = false;
  }

  /**
   * Check if haptic feedback is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Light haptic feedback (for subtle interactions like hover, slider)
   */
  light(): void {
    if (!this.enabled || !this.isSupported()) return;
    try {
      navigator.vibrate(10);
    } catch (error) {
      console.warn('[Haptic] Light vibration failed:', error);
    }
  }

  /**
   * Medium haptic feedback (for button clicks, selections)
   */
  medium(): void {
    if (!this.enabled || !this.isSupported()) return;
    try {
      navigator.vibrate(30);
    } catch (error) {
      console.warn('[Haptic] Medium vibration failed:', error);
    }
  }

  /**
   * Strong haptic feedback (for important actions, success, errors)
   */
  strong(): void {
    if (!this.enabled || !this.isSupported()) return;
    try {
      navigator.vibrate(50);
    } catch (error) {
      console.warn('[Haptic] Strong vibration failed:', error);
    }
  }

  /**
   * Success pattern (double vibration)
   */
  success(): void {
    if (!this.enabled || !this.isSupported()) return;
    try {
      navigator.vibrate([30, 50, 30]);
    } catch (error) {
      console.warn('[Haptic] Success vibration failed:', error);
    }
  }

  /**
   * Error pattern (long vibration)
   */
  error(): void {
    if (!this.enabled || !this.isSupported()) return;
    try {
      navigator.vibrate([50, 30, 50]);
    } catch (error) {
      console.warn('[Haptic] Error vibration failed:', error);
    }
  }

  /**
   * Notification pattern (triple vibration)
   */
  notification(): void {
    if (!this.enabled || !this.isSupported()) return;
    try {
      navigator.vibrate([20, 50, 20, 50, 20]);
    } catch (error) {
      console.warn('[Haptic] Notification vibration failed:', error);
    }
  }
}

// Export singleton instance
export const hapticManager = new HapticManager();
